import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-base-100">
      <header className="bg-neutral text-neutral-content py-4 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <h1
            className="text-3xl font-bold font-grotesk hover:opacity-80 transition-opacity cursor-pointer"
            onClick={() => navigate("/")}
          >
            PEAK
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="text-sm hover:underline"
          >
            Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <h1 className="text-4xl md:text-5xl font-bold font-grotesk mb-4">
          Privacy Policy
        </h1>
        <p className="text-base-content/70 mb-12">Effective Date: 23 September 2026</p>

        <div className="space-y-8 text-base-content">
          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              1. Introduction
            </h2>
            <p className="leading-relaxed mb-4">
              Peak (the "App") is operated by PEAK APP PTY LTD (ABN 34 701 745
              422) ("we", "us", or "our"). We are committed to protecting your
              privacy and handling your personal information responsibly and
              transparently.
            </p>
            <p className="leading-relaxed mb-4">
              This Privacy Policy explains how we collect, use, disclose, and
              protect your personal information when you use Peak, available at{" "}
              <a
                href="https://peak.app"
                className="text-blue-600 hover:underline"
              >
                https://peak.app
              </a>
              ,{" "}
              <a
                href="https://ams.peak.app"
                className="text-blue-600 hover:underline"
              >
                https://ams.peak.app
              </a>
              ,{" "}
              <a
                href="https://learn.peak.app"
                className="text-blue-600 hover:underline"
              >
                https://learn.peak.app
              </a>
              ,{" "}
              <a
                href="https://help.peak.app"
                className="text-blue-600 hover:underline"
              >
                https://help.peak.app
              </a>
              ,{" "}
              <a
                href="https://trust.peak.app"
                className="text-blue-600 hover:underline"
              >
                https://trust.peak.app
              </a>
              , and{" "}
              <a
                href="https://academy.peak.app"
                className="text-blue-600 hover:underline"
              >
                https://academy.peak.app
              </a>
              , and our mobile applications. It also describes your rights
              regarding your personal information and how you can contact us
              with questions or concerns.
            </p>
            <p className="leading-relaxed mb-4">
              We comply with the <em>Privacy Act 1988 (Cth)</em> and the
              Australian Privacy Principles (APPs). As we serve users
              internationally, we also take steps to comply with the European
              Union's General Data Protection Regulation (GDPR), the California
              Consumer Privacy Act (CCPA), and other applicable privacy laws.
            </p>
            <p className="leading-relaxed font-semibold">
              By using Peak, you acknowledge that you have read and understood
              this Privacy Policy. If you do not agree with our practices,
              please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              2. Scope of this policy
            </h2>
            <p className="leading-relaxed mb-4">
              This Privacy Policy applies to all personal information we collect
              through Peak, including our websites, mobile applications, and any
              related services, features, or content we provide (collectively,
              the "Services").
            </p>
            <p className="leading-relaxed">
              This policy does not apply to third-party websites, applications,
              or services that may be linked to or integrated with our Services.
              We encourage you to review the privacy policies of any third-party
              services you access.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              3. Personal information we collect
            </h2>
            <p className="leading-relaxed mb-4">
              We collect different types of personal information depending on
              how you interact with Peak. Under Australian privacy law,
              "personal information" means information or an opinion about an
              identified individual, or an individual who is reasonably
              identifiable.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.1 Information You Provide Directly
            </h3>
            <p className="leading-relaxed mb-3">
              When you create an account, use our Services, or contact us, you
              may provide us with the following information:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Account Information:</strong> Your name, email address,
              phone number, date of birth, gender, biological sex, and login
              credentials.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Profile Information:</strong> Athletic profile details,
              training preferences, goals, and settings you configure in the
              App.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Health and Training Data:</strong> Training logs, workout
              data, performance metrics, physical measurements, sleep data,
              recovery information, menstrual cycle data (if you choose to
              provide it), and other health-related information you input or
              that we derive from your use of the App. This is considered
              "sensitive information" under Australian privacy law and is
              afforded additional protections.
            </p>
            <p className="leading-relaxed">
              <strong>Communications:</strong> Information you provide when you
              contact our support team, respond to surveys, or communicate with
              us.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.2 Information Collected Automatically
            </h3>
            <p className="leading-relaxed mb-3">
              When you use our Services, we automatically collect certain
              technical and usage information:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Device Information:</strong> Device type, operating
              system, unique device identifiers, browser type, and mobile
              network information.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Usage Information:</strong> How you interact with the App,
              including pages visited, features used, actions taken, time spent,
              and browsing activity.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Location Data:</strong> General location information
              derived from your IP address. With your consent, we may also
              collect more precise location data to provide location-based
              features.
            </p>
            <p className="leading-relaxed">
              <strong>Log Data:</strong> IP address, access times, referring
              URLs, error logs, and other diagnostic data.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.3 Information from Third Parties
            </h3>
            <p className="leading-relaxed mb-3">
              We may receive personal information from third-party sources,
              including:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Social Login Providers:</strong> If you choose to sign in
              using Facebook, Google, or Apple, we receive your name, email
              address, and profile information as permitted by your privacy
              settings on those platforms.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Payment Processors:</strong> Our payment processor,
              Stripe, provides us with limited transaction information (such as
              confirmation of successful payments). We do not receive or store
              your full payment card details.
            </p>
            <p className="leading-relaxed">
              <strong>Wearable Device Providers:</strong> If you connect a wearable device such as a Garmin, we receive training, health and activity data directly from that provider in accordance with the permissions you grant. See Section 6 for how this data is handled in respect of AI and third-party processing.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              4. Sensitive information and health data
            </h2>
            <p className="leading-relaxed mb-4">
              Peak is a health and fitness application, and we collect health
              information to provide our core Services. Under the{" "}
              <em>Privacy Act 1988</em>, health information is classified as
              "sensitive information" and requires your express consent before
              collection (unless an exception applies).
            </p>
            <p className="leading-relaxed mb-2">
              The health-related information we collect may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                training and workout data, including exercise type, duration,
                intensity, and frequency;
              </li>
              <li>physical measurements and body composition data;</li>
              <li>performance metrics and athletic assessments;</li>
              <li>sleep, recovery, and wellness indicators;</li>
              <li>
                biological sex data for physiological calibration purposes; and
              </li>
              <li>
                any health-related notes or information you choose to share.
              </li>
            </ul>
            <p className="leading-relaxed font-semibold">
              By using Peak and providing this information, you expressly
              consent to our collection and use of your health data as described
              in this Privacy Policy. You may withdraw your consent at any time
              by contacting us or deleting your account, although this may
              affect your ability to use certain features of the App.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.1 Biological Sex Data Collection and Use
            </h3>
            <p className="leading-relaxed mb-4">
              Peak collects information about your biological sex for the sole
              purpose of accurately interpreting your physiological data.
              Biological sex influences how the body typically responds to
              training and recovery, including predictable differences in heart
              rate variability, resting heart rate, respiratory rate, sleep
              patterns, and workload adaptation.
            </p>
            <p className="leading-relaxed mb-4">
              Evidence suggests that biological sex provides the most reliable
              starting point for modelling physiological responses. Peak uses
              this as the initial baseline for interpreting readiness, recovery,
              and workload patterns. From there, Peak's individualisation
              algorithm adapts to your personal trends as you log consistently,
              ensuring that your insights become increasingly specific to your
              own physiology over time.
            </p>
            <p className="leading-relaxed mb-4">
              This information allows Peak to apply the correct physiological
              baselines when generating readiness scores and related insights,
              including peakScore, workloadFactor, and Health Load Ratio (HLR).
              This ensures that the insights you receive reflect how your body
              is likely to respond based on established physiological models.
            </p>
            <p className="leading-relaxed mb-4">
              Biological sex is not used for identity, display, social features,
              or any purpose unrelated to physiological interpretation.
            </p>
            <p className="leading-relaxed mb-2">
              We process biological sex information to:
            </p>
            <ul className="list-none pl-6 space-y-2 mb-4">
              <li>
                (a) calibrate physiological models, including peakScore,
                workloadFactor, and HLR;
              </li>
              <li>
                (b) interpret wearable data relative to established sex-based
                recovery patterns;
              </li>
              <li>
                (c) support personalised readiness, recovery, and performance
                insights; and
              </li>
              <li>
                (d) enable features that depend on accurate physiological
                baselines.
              </li>
            </ul>
            <p className="leading-relaxed">
              To ensure accurate insights, you should select the option that
              best reflects your underlying physiology. Updating this
              information may temporarily affect the accuracy of readiness
              baselines while new personalised data is collected.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.2 Menstrual Cycle Data (Where Applicable)
            </h3>
            <p className="leading-relaxed mb-4">
              For users who menstruate, Peak provides optional menstrual cycle
              logging and insights. These insights help individuals understand
              how different phases of the cycle may influence recovery, fatigue,
              performance, soreness, and workload tolerance.
            </p>
            <p className="leading-relaxed mb-4">
              Menstrual cycle tracking is optional, and cycle insights are
              generated only when cycle information is logged by the user. Peak
              does not infer menstrual cycle status from biological sex alone.
              Users who do not menstruate will never receive cycle-related
              insights.
            </p>
            <p className="leading-relaxed mb-4">
              Menstrual cycle information is classified as sensitive health data
              and is never shared with a coach unless you choose to enable that
              setting. This ensures that cycle-based insights are accurate,
              respectful, and user-controlled.
            </p>
            <p className="leading-relaxed">
              Menstrual cycle information, if used, should be updated truthfully
              for accurate cycle-related insights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              5. How we use your personal information
            </h2>
            <p className="leading-relaxed mb-4">
              We only collect and use your personal information where we have a
              lawful basis to do so. Our purposes for using your information
              include:
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.1 Providing and Improving Our Services
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Creating and managing your account;</li>
              <li>
                delivering personalised training insights, recommendations, and
                analytics based on your training data;
              </li>
              <li>
                powering our AI features, including the "Wizard" chatbot that
                provides personalised guidance and answers questions about your
                training data;
              </li>
              <li>processing payments and managing your subscription;</li>
              <li>
                providing customer support and responding to your inquiries;
              </li>
              <li>
                improving and developing new features, products, and services;
                and
              </li>
              <li>
                personalising your experience based on your preferences and
                usage patterns.
              </li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.2 Communications
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Sending you important service-related communications (such as
                account verification, security alerts, and changes to our terms
                or policies).
              </li>
              <li>
                With your consent, sending marketing communications about
                features, products, or services that may interest you. You can
                opt out of marketing communications at any time.
              </li>
              <li>
                Sending push notifications (with your consent) to provide
                training reminders, insights, or other relevant updates.
              </li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.3 Analytics and Research
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Analysing usage patterns to understand how our Services are used
                and to improve user experience.
              </li>
              <li>
                Conducting research and analysis using aggregated or
                de-identified data that does not identify you personally.
              </li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.4 Security and Legal Compliance
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Protecting our Services, users, and others from fraud, abuse,
                and security threats.
              </li>
              <li>
                Complying with applicable laws, regulations, and legal
                processes.
              </li>
              <li>Enforcing our Terms of Service and other agreements.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              6. Artificial intelligence and automated processing
            </h2>
            <p className="leading-relaxed mb-4">
              Peak uses artificial intelligence (AI) to enhance your experience
              and provide personalised insights. We want to be transparent about
              how we use AI in our Services:
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Personalised Insights:</strong> We use AI to analyse your
              training data and provide personalised recommendations,
              performance insights, and coaching suggestions tailored to your
              athletic profile and goals.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>The Wizard Chatbot:</strong> Our AI-powered assistant, the
              "Wizard," allows you to ask questions about your training data and
              receive contextual, personalised responses. When you interact with
              the Wizard, your training data and conversation context are
              processed to generate relevant answers.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Third-Party AI Services:</strong> We use Google's Gemini
              API to power certain AI features. When you use these features,
              relevant portions of your data (such as training metrics and
              conversation context) are sent to Google for processing. This data
              is processed in accordance with Google's privacy policies and our
              data processing agreements with them.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Garmin Data Exclusion:</strong> Data obtained from Garmin is not shared with, processed by, or otherwise made available to any third party, including Google’s Gemini API or any other external AI or data processing service referenced in this Section 6.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>Research-Based Insights:</strong> Our AI may also
              reference curated physiotherapy and sports science research to
              provide evidence-based recommendations.
            </p>
            <p className="leading-relaxed font-semibold">
              AI-generated insights are provided for informational purposes only
              and should not be considered medical advice. Always consult with
              qualified healthcare professionals before making decisions about
              your health or training.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              7. How we share your personal information
            </h2>
            <p className="leading-relaxed mb-4">
              We do not sell your personal information. We may share your
              information in the following circumstances:
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.1 Service Providers
            </h3>
            <p className="leading-relaxed mb-4">
              We engage trusted third-party service providers who perform
              services on our behalf, such as cloud hosting, payment processing,
              analytics, and customer support. These providers are contractually
              obligated to protect your information and use it only for the
              purposes we specify.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.2 AI Service Providers
            </h3>
            <p className="leading-relaxed mb-4">
              As described in Section 6, we share relevant data with Google (via
              the Gemini API) to power our AI features. Data shared includes
              training metrics, user queries, and contextual information
              necessary to generate personalised insights. This does not apply to data obtained from Garmin, which is excluded from sharing with Google or any other AI service provider as set out in Section 6. 
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.3 Analytics Providers
            </h3>
            <p className="leading-relaxed mb-4">
              We use Google Analytics and Meta Analytics to understand how users use Peak and to
              improve our Services and user experience.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.4 Payment Processors
            </h3>
            <p className="leading-relaxed mb-4">
              Payment transactions are processed by Stripe. When you make a
              payment, your payment information is provided directly to Stripe
              and is subject to Stripe's privacy policy. We do not store your
              full payment card details on our systems.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.5 Legal Requirements
            </h3>
            <p className="leading-relaxed mb-4">
              We may disclose your personal information when required by law,
              such as in response to a court order, subpoena, or other legal
              process, or when we believe disclosure is necessary to protect our
              rights, your safety, or the safety of others.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.6 Business Transfers
            </h3>
            <p className="leading-relaxed mb-4">
              If we are involved in a merger, acquisition, sale of assets, or
              bankruptcy, your personal information may be transferred as part
              of that transaction. We will notify you of any such change and any
              choices you may have regarding your information.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.7 With Your Consent
            </h3>
            <p className="leading-relaxed">
              We may share your information in other circumstances with your
              express consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              8. International data transfers
            </h2>
            <p className="leading-relaxed mb-4">
              Peak operates internationally and your personal information may be
              transferred to, stored, and processed in countries outside your
              country of residence, including Australia, the United States, and
              the European Union.
            </p>
            <p className="leading-relaxed mb-3">
              These countries may have data protection laws that differ from the
              laws of your country. When we transfer your information
              internationally, we take appropriate steps to ensure your
              information remains protected in accordance with this Privacy
              Policy and applicable law, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Using contractual protections with our service providers, such
                as standard contractual clauses approved by relevant
                authorities.
              </li>
              <li>
                Transferring to countries that have been recognised as providing
                adequate data protection.
              </li>
              <li>
                Implementing appropriate technical and organisational
                safeguards.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              9. Data security
            </h2>
            <p className="leading-relaxed mb-4">
              We take the security of your personal information seriously and
              implement appropriate technical and organisational measures to
              protect it from unauthorised access, disclosure, alteration, or
              destruction. Our security measures include:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Encryption:</strong> We use encryption to protect data in
              transit (using HTTPS/TLS) and at rest.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Access Controls:</strong> We implement strict access
              controls and authentication measures, including two-factor
              authentication, to limit access to personal information to
              authorised personnel only.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Infrastructure Security:</strong> We use firewalls, secure
              servers, and regular security audits to protect our systems.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Employee Training:</strong> Our team receives training on
              data protection and security best practices.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>Data Minimisation:</strong> We collect only the
              information necessary for the purposes described in this policy
              and implement anonymisation and pseudonymisation where
              appropriate.
            </p>
            <p className="leading-relaxed">
              While we strive to protect your personal information, no method of
              transmission over the internet or electronic storage is completely
              secure. We cannot guarantee absolute security, but we continuously
              work to improve our security practices.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.1 Protection of Biological Sex and Menstrual Cycle Data
            </h3>
            <p className="leading-relaxed mb-2">
              Biological sex and menstrual cycle data are treated as sensitive
              health information and are protected through:
            </p>
            <ul className="list-none pl-6 space-y-2 mb-4">
              <li>(a) encryption in transit and at rest;</li>
              <li>(b) restricted internal access;</li>
              <li>(c) strict access-control policies; and</li>
              <li>
                (d) data minimisation (used only for required algorithmic
                functions).
              </li>
            </ul>
            <p className="leading-relaxed">
              This information is never sold, never shared with third parties
              for marketing, and not visible to other users unless you
              explicitly share it with a coach.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              10. Data retention
            </h2>
            <p className="leading-relaxed mb-3">
              We retain your personal information for as long as your account is
              active or as needed to provide you with our Services. If you
              delete your account, we will delete or anonymise your personal
              information, unless:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                We are required to retain it for legal, regulatory, or
                compliance purposes;
              </li>
              <li>
                it is necessary to resolve disputes or enforce our agreements;
              </li>
              <li>it has been anonymised and can no longer identify you; or</li>
              <li>
                when personal information is no longer needed, we will securely
                delete or de-identify it in accordance with our data retention
                practices.
              </li>
            </ul>
            <p className="leading-relaxed">
              Biological sex and menstrual cycle data are retained only while
              your account remains active. If you delete your account, all
              related data is permanently removed in accordance with our data
              retention policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              11. Cookies and tracking technologies
            </h2>
            <p className="leading-relaxed mb-4">
              We use cookies and similar tracking technologies to collect
              information about your use of our Services and to enhance your
              experience.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              11.1 Types of Cookies We Use
            </h3>
            <p className="leading-relaxed mb-3">
              <strong>Strictly Necessary Cookies:</strong> Essential for the
              operation of our Services. These cookies enable core functionality
              such as security, network management, and account access. You
              cannot opt out of these cookies.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Performance Cookies:</strong> Help us understand how
              visitors interact with our Services by collecting information
              about pages visited, errors encountered, and loading times. This
              helps us improve our Services.
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Functional Cookies:</strong> Remember your preferences and
              settings (such as language and region) to provide a more
              personalised experience.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>Analytics Cookies:</strong> We use Google Analytics to
              collect and analyse information about how our Services are used.
              These cookies help us understand usage patterns and improve our
              Services.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              11.2 Managing Cookies
            </h3>
            <p className="leading-relaxed mb-4">
              You can manage your cookie preferences through our cookie consent
              banner when you first visit our site, or through your browser
              settings. Please note that disabling certain cookies may affect
              the functionality of our Services.
            </p>
            <p className="leading-relaxed">
              For more information about cookies and how to manage them, visit{" "}
              <a
                href="http://www.allaboutcookies.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                www.allaboutcookies.org
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              12. Your rights and choices
            </h2>
            <p className="leading-relaxed mb-4">
              You have various rights regarding your personal information. The
              specific rights available to you may depend on your location.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              12.1 Rights Under Australian Law
            </h3>
            <p className="leading-relaxed mb-3">
              Under the Australian Privacy Principles, you have the right to:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Access:</strong> Request access to the personal
              information we hold about you.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Correction:</strong> Request that we correct any
              inaccurate, incomplete, or out-of-date personal information.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>Complaints:</strong> Lodge a complaint about our handling
              of your personal information.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              12.2 Additional Rights (GDPR)
            </h3>
            <p className="leading-relaxed mb-3">
              If you are located in the European Economic Area or the United
              Kingdom, you may also have the right to:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Erasure:</strong> Request deletion of your personal
              information in certain circumstances.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Restriction:</strong> Request that we restrict processing
              of your personal information.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Data Portability:</strong> Request a copy of your personal
              information in a structured, commonly used, machine-readable
              format.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Object:</strong> Object to processing of your personal
              information for direct marketing or based on legitimate interests.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>Withdraw Consent:</strong> Withdraw your consent at any
              time where we rely on consent as the legal basis for processing.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              12.3 Additional Rights (California)
            </h3>
            <p className="leading-relaxed mb-4">
              If you are a California resident, the California Consumer Privacy
              Act (CCPA) provides you with additional rights, including the
              right to know what personal information we collect and how we use
              it, the right to request deletion, and the right to opt out of the
              sale of personal information (we do not sell personal
              information).
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              12.4 Exercising Your Rights
            </h3>
            <p className="leading-relaxed mb-4">
              To exercise any of these rights, please contact us at{" "}
              <a
                href="mailto:support@peak.app"
                className="text-blue-600 hover:underline"
              >
                support@peak.app
              </a>
              . We will respond to your request within 30 days. We may ask you
              to verify your identity before processing your request.
            </p>
            <p className="leading-relaxed mb-4">
              You may also access, update, or delete certain personal
              information directly through your account settings in the App.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              12.5 Marketing Preferences
            </h3>
            <p className="leading-relaxed mb-3">
              You can opt out of receiving marketing communications from us by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Clicking the "unsubscribe" link in any marketing email.</li>
              <li>
                Adjusting your notification preferences in the App settings.
              </li>
              <li>
                Contacting us at{" "}
                <a
                  href="mailto:support@peak.app"
                  className="text-blue-600 hover:underline"
                >
                  support@peak.app
                </a>
                .
              </li>
            </ul>
            <p className="leading-relaxed">
              Please note that even if you opt out of marketing communications,
              we may still send you service-related communications that are
              necessary for your use of Peak.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              13. Lawful basis for processing biological sex and menstrual cycle
              data
            </h2>
            <p className="leading-relaxed mb-4">
              We process biological sex and menstrual cycle data under the
              following legal bases:
            </p>
            <p className="leading-relaxed mb-3">
              <strong>Performance of a contract:</strong> Accurate readiness,
              recovery, and training insights require correct physiological
              baselines.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>User consent:</strong> Creating an account and completing
              your profile indicates consent to the processing of this
              information for the purposes described.
            </p>
            <p className="leading-relaxed">
              Where applicable under local data protection laws (such as GDPR),
              biological sex and menstrual cycle data may be classified as
              sensitive personal information. Peak applies heightened safeguards
              accordingly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              14. Children's privacy
            </h2>
            <p className="leading-relaxed">
              Peak is designed for athletes and individuals interested in
              athletic training. If you are a parent or guardian and believe
              your child has provided us with personal information without your
              consent, please contact us at{" "}
              <a
                href="mailto:support@peak.app"
                className="text-blue-600 hover:underline"
              >
                support@peak.app
              </a>{" "}
              and we will take steps to delete that information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              15. Third-Party services and links
            </h2>
            <p className="leading-relaxed mb-4">
              Our Services may contain links to third-party websites or
              integrate with third-party services. This Privacy Policy does not
              apply to those third-party services, and we are not responsible
              for their privacy practices. We encourage you to read the privacy
              policies of any third-party services you use.
            </p>
            <p className="leading-relaxed">
              When you use social login features (Facebook, Google, or Apple),
              the information we receive is governed by the privacy settings you
              have configured with those providers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              16. Changes to this Privacy Policy
            </h2>
            <p className="leading-relaxed mb-3">
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, technology, legal requirements, or other
              factors. When we make material changes, we will notify you by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Sending you an email notification.</li>
              <li>Displaying a banner or notification on our website.</li>
              <li>Posting an in-app notification.</li>
            </ul>
            <p className="leading-relaxed">
              We encourage you to review this Privacy Policy periodically. The
              "Effective Date" at the top of this policy indicates when it was
              last updated. Your continued use of our Services after any changes
              constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              17. Complaints
            </h2>
            <p className="leading-relaxed mb-4">
              If you have concerns about how we have handled your personal
              information, we encourage you to contact us first so we can try to
              resolve your concerns.
            </p>
            <p className="leading-relaxed mb-3">
              If you are not satisfied with our response, you may lodge a
              complaint with the relevant supervisory authority:
            </p>
            <p className="leading-relaxed mb-2">
              <strong>Australia:</strong> Office of the Australian Information
              Commissioner (OAIC) at{" "}
              <a
                href="http://www.oaic.gov.au"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                www.oaic.gov.au
              </a>{" "}
              or by phone at 1300 363 992.
            </p>
            <p className="leading-relaxed mb-2">
              <strong>European Union:</strong> Your local Data Protection
              Authority. A list is available at{" "}
              <a
                href="https://edpb.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                edpb.europa.eu
              </a>
              .
            </p>
            <p className="leading-relaxed">
              <strong>United Kingdom:</strong> Information Commissioner's Office
              (ICO) at{" "}
              <a
                href="https://ico.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                ico.org.uk
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              18. Contact us
            </h2>
            <p className="leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this
              Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-base-200 p-4 rounded-lg">
              <p className="mb-1">
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:support@peak.app"
                  className="text-blue-600 hover:underline"
                >
                  support@peak.app
                </a>
              </p>
              <p className="mb-1">
                <strong>Website:</strong>{" "}
                <a
                  href="https://peak.app"
                  className="text-blue-600 hover:underline"
                >
                  https://peak.app
                </a>
              </p>
              <p className="mb-1">
                <strong>Entity:</strong> PEAK APP PTY LTD
              </p>
              <p>
                <strong>ABN:</strong> 34 701 745 422
              </p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-base-300">
          <p className="text-sm text-base-content/70 text-center">
            By using Peak, you agree to the terms outlined in this Privacy
            Policy.
          </p>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-base-100 border-t border-base-300">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Logo */}
            <div className="flex flex-col items-center md:items-start gap-4">
              <div className="text-3xl  font-groteskLogo font-bold">
                PEAK<span className="font-light text-base-content/60">^</span>
              </div>
              <p className="text-base-content/70 text-sm">
                © {new Date().getFullYear()} Peak. All rights reserved.
              </p>
            </div>

            {/* Links */}
            <div className="flex gap-6 text-sm">
              <a
                href="/privacy-policy"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
